/**
 * Page-scoped teardown registry.
 *
 * Pages can register cleanup functions (event handlers, store
 * subscriptions, observers) and the router flushes them before each
 * new route handler runs. Prevents listener accumulation across long
 * SPA sessions (BETA-00173).
 *
 * Direct registry calls are also supported for non-store teardowns —
 * e.g. `pageScope.register(() => observer.disconnect())`.
 */
class PageScope {
  constructor() {
    this._teardowns = new Set();
  }

  register(fn) {
    if (typeof fn === 'function') this._teardowns.add(fn);
    return fn;
  }

  flush() {
    for (const fn of this._teardowns) {
      try {
        fn();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[pageScope] teardown error:', err);
      }
    }
    this._teardowns.clear();
  }
}

export const pageScope = new PageScope();

/**
 * Central state store with observer pattern.
 * Simple pub/sub reactive state management.
 */
class Store {
  constructor() {
    this._state = {};
    this._listeners = new Map();
  }

  get(key) {
    return this._state[key];
  }

  set(key, value) {
    const old = this._state[key];
    this._state[key] = value;
    this._notify(key, value, old);
  }

  update(key, fn) {
    const old = this._state[key];
    const next = fn(structuredClone(old));
    this._state[key] = next;
    this._notify(key, next, old);
  }

  on(key, callback) {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    this._listeners.get(key).add(callback);
    return () => this._listeners.get(key)?.delete(callback);
  }

  // Alias for `on` — matches the documented API surface and is the
  // preferred name for page-level code. Both forms return an
  // unsubscribe function.
  subscribe(key, callback) {
    return this.on(key, callback);
  }

  // Subscribe with automatic teardown on the next page navigation.
  // BETA-00173 — long SPA sessions previously accumulated dead listeners
  // because pages had no teardown hook. Pages should prefer this over
  // `subscribe` for any subscription scoped to a single view; the
  // ui-kit router flushes registered teardowns before each new handler
  // runs, so listeners cannot leak across navigations.
  subscribeForPage(key, callback) {
    const unsubscribe = this.on(key, callback);
    pageScope.register(unsubscribe);
    return unsubscribe;
  }

  _notify(key, value, old) {
    const listeners = this._listeners.get(key);
    if (listeners) {
      for (const cb of listeners) {
        try {
          cb(value, old);
        } catch (err) {
          console.error(`Store listener error for "${key}":`, err);
        }
      }
    }
  }
}

export const store = new Store();

// Default state
store.set('user', null);
store.set('isAuthenticated', false);
store.set('sidebarCollapsed', false);
store.set('toasts', []);
store.set('currentPage', '/');
